import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  BriefApplicationResponseDto,
  SocialChannelEnum,
} from '@/app/common/general-types/briefs.dto';
import { CreatorResponseDto } from '@/app/common/general-types/creators.dto';
import {
  CreateSubmittedPostDto,
  SubmittedPostDto,
  SubmittedPostVersionEnum,
} from '@/app/common/general-types/submitted-posts.dto';
import { VideoResponseDto } from '@/app/common/general-types/videos.dto';
import { EmptyStateComponent } from '@/app/atoms/empty-state/empty-state.component';
import { VideoThumbnailComponent } from '@/app/atoms/video-thumbnail/video-thumbnail.component';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { SubmittedPostsService } from '@/app/services/submitted-posts/submitted-posts.service';
import { CreatorActionPanel } from '../action-panel/action-panel';
import {
  creatorHasPlatform,
  findSubmittedPostForVideo,
  getSocialChannelLabel,
  resolveCreatorPlatformHandle,
  getSubmittablePostKey,
  patchSubmittablePost,
  platformRequiresAdCode,
} from '../application-actions/application-actions.helpers';
import { SubmittablePostsMap, SelectedSocialPostEvent } from '../application-actions/application-actions.types';
import { CreatorSelectPostInstagram } from '../select-post-instagram/select-post-instagram';
import { CreatorSelectPostTiktok } from '../select-post-tiktok/select-post-tiktok';

@Component({
  selector: 'ins-creator-post-on-platform',
  imports: [
    FormsModule,
    CreatorActionPanel,
    EmptyStateComponent,
    VideoThumbnailComponent,
    CreatorSelectPostTiktok,
    CreatorSelectPostInstagram,
  ],
  templateUrl: './post-on-platform.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class CreatorPostOnPlatform {
  application = input.required<BriefApplicationResponseDto>();
  creator = input.required<CreatorResponseDto>();
  approvedVideos = input.required<VideoResponseDto[]>();
  submittedPosts = input.required<SubmittedPostDto[]>();
  submittablePosts = input.required<SubmittablePostsMap>();
  /** Primary platform uses brief.shoutBrief.socialPlatform; cross-post passes an explicit platform. */
  platform = input<number | undefined>(undefined);
  mode = input<'primary' | 'cross'>('primary');

  submittablePostsChange = output<SubmittablePostsMap>();
  submittedPostsChange = output<SubmittedPostDto[]>();

  readonly expanded = model(false);
  readonly submittingVideoId = signal<string | undefined>(undefined);
  readonly selectingForVideoId = signal<string | undefined>(undefined);
  readonly selectedPostPreview = signal<Record<string, SelectedSocialPostEvent['post']>>({});

  readonly resolvedPlatform = computed(() => {
    if (this.mode() === 'cross') {
      return Number(this.platform());
    }
    return Number(this.application().brief?.shoutBrief?.socialPlatform);
  });

  readonly panelTitle = computed(() => {
    const label = getSocialChannelLabel(this.resolvedPlatform());
    return this.mode() === 'primary' ? `Post on ${label}` : `Re-post on ${label}`;
  });

  readonly panelSubtitle = computed(() =>
    this.mode() === 'primary' ? 'Primary platform' : 'Cross platform',
  );

  readonly creatorHasRequiredPlatform = computed(() =>
    creatorHasPlatform(this.creator(), this.resolvedPlatform()),
  );

  readonly platformHandle = computed(() =>
    resolveCreatorPlatformHandle(this.creator(), this.resolvedPlatform()),
  );

  readonly SocialChannelEnum = SocialChannelEnum;
  readonly getSocialChannelLabel = getSocialChannelLabel;

  private submittedPostsService = inject(SubmittedPostsService);
  private toastNotificationService = inject(ToastNotificationService);

  private storageKey(videoId: string): string {
    return this.mode() === 'cross'
      ? getSubmittablePostKey(videoId, this.resolvedPlatform())
      : videoId;
  }

  getLinkValue(videoId: string): string {
    const existing = findSubmittedPostForVideo(
      this.submittedPosts(),
      videoId,
      this.mode() === 'cross' ? this.resolvedPlatform() : undefined,
    );
    if (existing?.videoLink) {
      return existing.videoLink;
    }
    return this.submittablePosts()[this.storageKey(videoId)]?.videoLink ?? '';
  }

  getAdCodeValue(videoId: string): string {
    return this.submittablePosts()[this.storageKey(videoId)]?.adCode ?? '';
  }

  isSubmitted(videoId: string): boolean {
    const post = this.submittablePosts()[this.storageKey(videoId)];
    if (post?.hasBeenSubmitted) {
      return true;
    }

    return Boolean(
      findSubmittedPostForVideo(
        this.submittedPosts(),
        videoId,
        this.mode() === 'cross' ? this.resolvedPlatform() : undefined,
      ),
    );
  }

  updateLinkValue(videoId: string, value: string): void {
    this.submittablePostsChange.emit(
      patchSubmittablePost(this.submittablePosts(), this.storageKey(videoId), { videoLink: value }),
    );
  }

  updateAdCodeValue(videoId: string, value: string): void {
    this.submittablePostsChange.emit(
      patchSubmittablePost(this.submittablePosts(), this.storageKey(videoId), { adCode: value }),
    );
  }

  openPostSelector(videoId: string): void {
    this.selectingForVideoId.set(videoId);
  }

  onSelectedPost(event: SelectedSocialPostEvent): void {
    const videoId = event.appVid.id;
    const postId =
      this.resolvedPlatform() === SocialChannelEnum['Instagram Reel'] ||
      this.resolvedPlatform() === SocialChannelEnum['Instagram Story']
        ? event.post.post_id
        : event.post.id;

    this.selectedPostPreview.update((current) => ({ ...current, [videoId]: event.post }));
    this.selectingForVideoId.set(undefined);
    this.submittablePostsChange.emit(
      patchSubmittablePost(this.submittablePosts(), this.storageKey(videoId), {
        videoLink: event.post.share_url,
        postId,
      }),
    );
  }

  submitSocialPost(videoId: string): void {
    const app = this.application();
    const shoutBrief = app.brief?.shoutBrief;
    if (!shoutBrief) {
      return;
    }

    const platform = this.resolvedPlatform();
    const videoLink = this.getLinkValue(videoId);
    const adCode = this.getAdCodeValue(videoId);
    const postId = this.submittablePosts()[this.storageKey(videoId)]?.postId;

    const payload: CreateSubmittedPostDto = {
      briefApplicationId: app.id,
      briefId: app.briefId,
      creatorId: app.creatorUserId,
      createdDate: new Date(),
      socialChannel: platform,
      videoLink,
      adCode,
      videoId,
      shoutBriefId: shoutBrief.id,
      // Collect through the brief's own pipeline; new briefs default to Ultra Social.
      resultsVersion: shoutBrief.resultsVersion ?? SubmittedPostVersionEnum.UltraSocial,
      postId,
    };

    this.submittingVideoId.set(videoId);

    this.submittedPostsService.createSubmittedPost(payload).subscribe({
      next: (created) => {
        this.submittingVideoId.set(undefined);
        this.submittedPostsChange.emit([...this.submittedPosts(), created]);
        this.submittablePostsChange.emit(
          patchSubmittablePost(this.submittablePosts(), this.storageKey(videoId), {
            videoLink,
            adCode,
            hasBeenSubmitted: true,
            socialChannel: platform,
            postId,
          }),
        );
        void this.toastNotificationService.alert({
          title: 'Post submitted',
          message: `Submitted ${getSocialChannelLabel(platform)} link.`,
          type: 'success',
          icon: 'success',
        });
      },
      error: () => {
        this.submittingVideoId.set(undefined);
        void this.toastNotificationService.alert({
          title: 'Submit failed',
          message: 'Could not submit the social post link.',
          type: 'error',
          icon: 'error',
        });
      },
    });
  }

  requiresAdCode(): boolean {
    return platformRequiresAdCode(this.resolvedPlatform());
  }

  canUseTikTokSelector(videoId: string): boolean {
    return (
      this.selectingForVideoId() === videoId &&
      this.resolvedPlatform() === SocialChannelEnum['TikTok Post'] &&
      Boolean(this.creator().ssCreator?.tiktokAccountId)
    );
  }

  canUseInstagramSelector(videoId: string): boolean {
    return (
      this.selectingForVideoId() === videoId &&
      (this.resolvedPlatform() === SocialChannelEnum['Instagram Reel'] ||
        this.resolvedPlatform() === SocialChannelEnum['Instagram Story']) &&
      Boolean(this.creator().ssCreator?.instagramAccountId)
    );
  }

  selectedPreview(videoId: string): SelectedSocialPostEvent['post'] | undefined {
    return this.selectedPostPreview()[videoId];
  }
}
